import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, Sparkles } from "lucide-react";

// Preset reasoning traces for the different synthesis contexts.
export const REASONING = {
  portfolio: {
    title: "Analyzing enterprise portfolio",
    steps: [
      "Ingesting latest program updates",
      "Correlating cross-system dependencies",
      "Evaluating delivery confidence",
      "Generating executive recommendations",
    ],
  },
  simulate: {
    title: "Simulating alternatives",
    steps: [
      "Modeling alternative allocation paths",
      "Projecting impact across programs",
      "Scoring trade-offs and confidence",
    ],
  },
  analysis: {
    title: "Compiling supporting analysis",
    steps: [
      "Gathering supporting signals",
      "Correlating risk and budget data",
      "Synthesizing the executive analysis",
    ],
  },
  briefing: {
    title: "Preparing your executive brief",
    steps: [
      "Scanning active programs and integrations",
      "Correlating cross-system dependencies",
      "Evaluating delivery confidence and risk",
      "Composing your executive brief",
    ],
  },
  // Full cognitive-substrate pipeline — how a decision moves through NEXSYS.
  orchestra: {
    title: "Reasoning through the cognitive substrate",
    steps: [
      { label: "Decision Received", sub: "Framing the question" },
      { label: "Awareness", sub: "Sensing enterprise state" },
      { label: "Semantic Resolution", sub: "Resolving entities & intent" },
      { label: "Enterprise State Assessment", sub: "Reading live portfolio & signals" },
      { label: "Specialized Agent Deliberation", sub: "10 agents deliberating" },
      { label: "Governance Review", sub: "Validating against policy" },
      { label: "Decision Object", sub: "Assembling the governed decision" },
      { label: "Executive Summary", sub: "Composing the verdict" },
      { label: "Knowledge & Memory Update", sub: "Persisting to enterprise memory" },
    ],
  },
};

const norm = (s) => (typeof s === "string" ? { label: s, sub: "" } : s);

// Default cadence for the cognitive-substrate pipeline (ms per stage).
export const ORCHESTRA_CADENCE = 480;

// A staged "live reasoning" indicator — auto-advances through steps so users see the
// platform actively synthesizing data (not just retrieving it), even when it finishes fast.
// `spine` renders a connected vertical pipeline (used for the cognitive-substrate flow).
export function ReasoningStream({ preset = "portfolio", title, steps, compact = false, cadence = 650, spine = false }) {
  const trace = REASONING[preset] || REASONING.portfolio;
  const label = title || trace.title;
  const items = (steps || trace.steps).map(norm);
  const [active, setActive] = useState(0);

  useEffect(() => {
    // advance one past the last index so the final stage also earns its checkmark
    if (active >= items.length) return;
    const t = setTimeout(() => setActive((i) => Math.min(i + 1, items.length)), cadence);
    return () => clearTimeout(t);
  }, [active, items.length, cadence]);

  return (
    <div
      className={`w-full ${compact ? "text-[12px]" : "text-[13px]"}`}
      data-testid="reasoning-stream"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 text-cyan-200/90">
        <span className="relative flex h-4 w-4 items-center justify-center">
          <Sparkles className="h-3.5 w-3.5" />
          <span className="absolute inset-0 -z-10 rounded-full bg-cyan-400/40 blur-[6px] nx-pulse" />
        </span>
        <span className="font-medium">{label}</span>
        <span className="nx-reason-dots font-mono text-cyan-300/70">
          <span>.</span><span>.</span><span>.</span>
        </span>
      </div>

      <div className={`relative mt-2.5 ${spine ? "pl-0.5" : compact ? "pl-1" : "pl-0.5"}`}>
        {spine && (
          <span className="pointer-events-none absolute left-[8px] top-2 bottom-2 w-px bg-gradient-to-b from-emerald-400/50 via-cyan-400/40 to-white/10" />
        )}
        <div className={spine ? "space-y-2" : "space-y-1.5"}>
          <AnimatePresence initial={false}>
            {items.slice(0, active + 1).map((s, i) => {
              const complete = i < active;
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                  className="relative flex items-start gap-2.5"
                  data-testid="reasoning-step"
                >
                  <span className={`relative z-10 flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-full ${spine ? "bg-[#0b111c] ring-1 ring-white/10" : ""}`}>
                    {complete ? (
                      <motion.span
                        initial={{ scale: 0.4, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 420, damping: 20 }}
                      >
                        <Check className="h-3.5 w-3.5 text-emerald-400" />
                      </motion.span>
                    ) : (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-300" />
                    )}
                  </span>
                  <span className="min-w-0 leading-tight">
                    <span className={`block font-medium ${complete ? "text-white/55" : "text-white/90"}`}>{s.label}</span>
                    {s.sub && <span className={`block text-[10px] ${complete ? "text-white/25" : "text-cyan-300/60"}`}>{s.sub}</span>}
                  </span>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
